import { Resume, StepByStepFlowStepId } from 'packages/types'
import { i18n as I18n } from 'builder/utils/i18n'
import { SectionNames } from 'builder/modules/resumeEditor'
import Card from 'builder/components/Card'
import { WorkExperienceKeywordsToggler } from 'builder/components/AiFeatures/Keywords'
import { constructSkillsSectionData } from 'builder/components/SectionsList'
import { SectionData } from 'builder/components/Section'

import { sectionTitleTranslation } from '../../DocumentEditor/ATSResumeEditor/builderSectionsConfig'
import { TemplateName } from '../../Helper/constants'
import {
  defaultSectionOrder,
  shanghaiSectionOrder,
  pragueSectionOrder,
  athensSectionOrder,
} from './constants'
export type StepByStepSectionData = Omit<SectionData, 'id'> & {
  id: StepByStepFlowStepId
  isAdditionalSection?: boolean
}

export function constructSectionsData(
  resume: Resume,
  isAiResumeReviewFeatureActive?: boolean,
): {
  activeSections: StepByStepSectionData[]
  additionalSections: StepByStepSectionData[]
} {
  const { sectionsOrder, template } = resume

  const templateSectionOrder =
    template === TemplateName.Athens
      ? athensSectionOrder
      : template === TemplateName.Prague
      ? pragueSectionOrder
      : template === TemplateName.Shanghai
      ? shanghaiSectionOrder
      : defaultSectionOrder

  // Extract and append custom sections
  const customSections = sectionsOrder.filter(section => section.startsWith('custom:'))

  const finalSectionOrder = [...templateSectionOrder, ...customSections]

  const sectionTitle = sectionTitleTranslation(template, I18n.t)
  const isTemplatePrague = template === TemplateName.Prague
  const isTemplateShanghai = template === TemplateName.Shanghai
  const isTemplateAthens = template === TemplateName.Athens
  const isAtsTemplate = isTemplatePrague || isTemplateAthens || isTemplateShanghai

  const sections: StepByStepSectionData[] = [
    {
      id: SectionNames.details,
      title: '',
      CardComponent: () => null,
    },
    {
      id: SectionNames.workExperiences,
      title: sectionTitle(SectionNames.workExperiences),
      customTitle: resume.sectionTitles.workExperiences,
      addButtonText: resume.workExperiences.length
        ? I18n.t('builder.resume_editor.work_experience_section.add_one_more')
        : I18n.t('builder.resume_editor.work_experience_section.add'),
      CardComponent: Card.WorkExperience,
      items: resume.workExperiences,
      hintText: I18n.t('builder.resume_editor.work_experience_section.hint'),
      renamable: true,
      badgeComponents: [WorkExperienceKeywordsToggler],
    },
    {
      id: SectionNames.educations,
      title: sectionTitle(SectionNames.educations),
      customTitle: resume.sectionTitles.educations,
      addButtonText: resume.educations.length
        ? I18n.t('builder.resume_editor.educations_section.add_one_more')
        : I18n.t('builder.resume_editor.educations_section.add'),
      CardComponent: Card.Education,
      items: resume.educations,
      hintText: I18n.t('builder.resume_editor.educations_section.hint'),
      renamable: true,
    },
    constructSkillsSectionData(resume),
    {
      id: SectionNames.powerStatement,
      title: I18n.t('builder.resume_editor.power_statement_section.title'),
      customTitle: resume.sectionTitles.powerStatement,
      hidden: !isTemplateShanghai,
      CardComponent: () => null,
    },
    {
      id: SectionNames.profile,
      title: I18n.t('builder.resume_editor.professional_summary.title'),
      customTitle: resume.sectionTitles.profile,
      CardComponent: () => null,
    },
    {
      id: SectionNames.accomplishments,
      single: true,
      title: sectionTitle(SectionNames.accomplishments),
      customTitle: resume.sectionTitles.accomplishments,
      CardComponent: Card.Accomplishments,
      items: [
        { id: 'accomplishments', cid: 'accomplishments', description: resume.accomplishments },
      ],
      renamable: true,
      hidden: !isTemplateShanghai,
    },
    {
      id: SectionNames.courses,
      title: sectionTitle(SectionNames.courses),
      customTitle: resume.sectionTitles.courses,
      addButtonText: resume.courses.length
        ? I18n.t('builder.resume_editor.courses_section.add_one_more')
        : I18n.t('builder.resume_editor.courses_section.add'),
      CardComponent: Card.Course,
      items: resume.courses,
      renamable: true,
      removable: true,
      hidden: resume.courses.length === 0,
      isAdditionalSection: true,
    },
    {
      id: SectionNames.internships,
      title: sectionTitle(SectionNames.internships),
      customTitle: resume.sectionTitles.internships,
      addButtonText: resume.internships.length
        ? I18n.t('builder.resume_editor.internships_section.add_one_more')
        : I18n.t('builder.resume_editor.internships_section.add'),
      CardComponent: Card.WorkExperience,
      items: resume.internships,
      renamable: true,
      removable: true,
      hidden: resume.internships.length === 0,
      isAdditionalSection: true,
    },
    {
      id: SectionNames.activities,
      title: sectionTitle(SectionNames.activities),
      customTitle: resume.sectionTitles.activities,
      addButtonText: resume.activities.length
        ? I18n.t('builder.resume_editor.activities_section.add_one_more')
        : I18n.t('builder.resume_editor.activities_section.add'),
      CardComponent: Card.Activity,
      items: resume.activities,
      renamable: true,
      removable: true,
      hidden: resume.activities.length === 0,
      isAdditionalSection: true,
    },
    {
      id: SectionNames.hobbies,
      title: sectionTitle(SectionNames.hobbies),
      customTitle: resume.sectionTitles.hobbies,
      single: true,
      CardComponent: Card.Hobby,
      items: resume.hobbies,
      renamable: true,
      removable: true,
      hidden: isAtsTemplate || resume.hobbies.length === 0,
      isAdditionalSection: true,
    },
    {
      id: SectionNames.languages,
      title: sectionTitle(SectionNames.languages),
      customTitle: resume.sectionTitles.languages,
      addButtonText: resume.languages.length
        ? I18n.t('builder.resume_editor.languages_section.add_one_more')
        : I18n.t('builder.resume_editor.languages_section.add'),
      CardComponent: Card.Language,
      items: resume.languages,
      renamable: true,
      removable: true,
      hidden: isAtsTemplate || resume.languages.length === 0,
      isAdditionalSection: true,
    },
    {
      id: SectionNames.socialProfiles,
      title: sectionTitle(SectionNames.socialProfiles),
      customTitle: resume.sectionTitles.socialProfiles,
      addButtonText: resume.socialProfiles.length
        ? I18n.t('builder.resume_editor.links_section.add_one_more')
        : I18n.t('builder.resume_editor.links_section.add'),
      CardComponent: Card.Social,
      items: resume.socialProfiles,
      renamable: true,
      hintText: I18n.t('builder.resume_editor.links_section.hint'),
      hidden: isAtsTemplate || resume.socialProfiles.length === 0,
      isAdditionalSection: true,
    },
    {
      id: SectionNames.references,
      title: sectionTitle(SectionNames.references),
      customTitle: resume.sectionTitles.references,
      addButtonText: resume.references.length
        ? I18n.t('builder.resume_editor.references_section.add_one_more')
        : I18n.t('builder.resume_editor.references_section.add'),
      CardComponent: Card.Reference,
      items: resume.references,
      renamable: true,
      removable: true,
      hidden:
        isAtsTemplate || (!sectionsOrder.includes('references') && resume.references.length === 0),
      isAdditionalSection: true,
    },
    {
      id: SectionNames.addAdditionalSection,
      title: I18n.t(
        'builder.resume_editor.step_by_step_editor.navigation_container.additional_sections',
      ),
      CardComponent: () => null,
    },
    {
      id: 'aiResumeReview',
      title: I18n.t('builder.resume_editor.step_by_step_editor.navigation_container.ai_review'),
      CardComponent: () => null,
      hidden: !isAiResumeReviewFeatureActive,
    },
    ...resume.customSections.map(section => ({
      id: section.id || Number(section.externalId),
      title: section.title || I18n.t('builder.resume_editor.untitled'),
      customTitle: section.title,
      addButtonText: section.items.length
        ? I18n.t('builder.resume_editor.add_one_more_item')
        : I18n.t('builder.resume_editor.add_item'),
      CardComponent: Card.Custom,
      items: section.items,
      removable: true,
      renamable: true,
      isCustom: true,
      externalId: section.externalId,
      sortId: `custom:${section.externalId}`,
    })),
  ]

  const additionalSection = sections.find(
    section => section.id === SectionNames.addAdditionalSection,
  )

  const aiResumeReviewSection = sections.find(section => section.id === 'aiResumeReview')

  return {
    activeSections: [
      ...sections
        .filter(section => section.id !== SectionNames.addAdditionalSection && !section.hidden)
        .filter(section => section.id !== 'aiResumeReview' && !section.hidden)
        .sort((a, b) => {
          return (
            finalSectionOrder.indexOf(a.sortId || (a.id as string)) -
            finalSectionOrder.indexOf(b.sortId || (b.id as string))
          )
        }),
      ...(additionalSection ? [additionalSection] : []),
      ...(aiResumeReviewSection && !aiResumeReviewSection.hidden ? [aiResumeReviewSection] : []),
    ],
    additionalSections: sections.filter(section => section.isAdditionalSection === true),
  }
}
